import { CheckIcon, FileUploader, Note, SubHeading, Tab, Tabs } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { InfoTooltip } from 'src/components/info-tooltip';
import { useUserPermissions } from 'src/manager/hooks/user-permissions';

import { State, useGetWebcastPresentationsQuery } from '../../../../../generated/graphql-manager';
import { getLanguageKey } from '../../../../../utils';
import { getPresentationStatus, usePresentationsProcessor } from '../../../../providers/presentations-processor';
import { MediaAccordion } from '../media-accordion';
import { Presentation } from '../presentation';

import styles from '../media.module.css';

type Props = {
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
};

export function Presentations({ onLoadStart, onLoadEnd }: Props) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const { isEventEditingAllowed } = useUserPermissions();
  const { uploadPresentation } = usePresentationsProcessor();

  const { data, refetch } = useGetWebcastPresentationsQuery({ variables: { webcastId } });
  const primaryLang = data?.webcast?.primaryLang;

  if (!primaryLang) return null;

  const additionalLangs = data?.webcast?.additionalLangs || [];
  const languages = [primaryLang, ...additionalLangs].filter((language) => language.__typename === 'StandardLanguage');
  const isMultiLanguage = languages.length > 1;
  const presentations = data?.webcast?.presentations || [];
  const hasContent = presentations.length > 0;
  const allUploaded = hasContent && isMultiLanguage && presentations.length % languages.length === 0;
  const formDisabled = data?.webcast.state === State.ARCHIVED || !isEventEditingAllowed;
  const disablePresentationDeletion = formDisabled || data?.webcast.state !== State.PRELIVE;
  const groupIds = presentations.map((presentation) => presentation.groupId);
  const uniqueGroupIds = Array.from(new Set(groupIds));
  const noteVisible = isMultiLanguage && presentations.length !== languages.length * uniqueGroupIds.length;

  const additionalSetOpenLabel = (
    <>
      {t('manager.webcastSetup.media.hideUpload')}
      {hasContent && <InfoTooltip label={t('manager.webcastSetup.media.presentation.multiplePresentationsTooltip')} />}
    </>
  );

  const additionalSetCloseLabel = (
    <>
      {hasContent
        ? t('manager.webcastSetup.media.uploadAdditionalSlides')
        : t('manager.webcastSetup.media.uploadSlides')}
      {hasContent && <InfoTooltip label={t('manager.webcastSetup.media.presentation.multiplePresentationsTooltip')} />}
    </>
  );

  const fileUploader = (language: string, groupId?: string) => (
    <FileUploader
      testIdPrefix="presentation-"
      acceptedFormats=".pdf"
      maximumFileSize={104857600} // 100MB
      disabled={formDisabled}
      icon={null}
      onLoadStart={onLoadStart}
      onLoadEnd={onLoadEnd}
      additionalUploadInformation={[
        t('manager.webcastSetup.media.languageContent.fileUploader.additionalFileRestrictions'),
      ]}
      onChange={(files) => {
        uploadPresentation({
          webcastId,
          language,
          groupId,
          file: Array.isArray(files) ? files[0] : files,
          onSuccess: refetch,
        });
      }}
    />
  );

  const getLanguageLabel = (languageCode: string) => {
    const translatedLanguage = t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(languageCode)}`);

    return languageCode === primaryLang.code
      ? t('manager.webcastSetup.media.primaryLanguage', { primaryLanguage: translatedLanguage })
      : translatedLanguage;
  };

  return (
    <div className={styles.wrapper}>
      <SubHeading>{t('manager.webcastSetup.media.presentationsHeading')}</SubHeading>

      {!isMultiLanguage && (
        <div className={styles.presentationsList}>
          {presentations.map((presentation) => (
            <Presentation
              key={presentation.id}
              presentation={presentation}
              disablePresentationDeletion={disablePresentationDeletion}
              onLoadStart={onLoadStart}
              onLoadEnd={onLoadEnd}
            />
          ))}
        </div>
      )}

      {!isMultiLanguage && (
        <MediaAccordion
          key={presentations.length}
          testId="presentations"
          visible
          buttonLabel={t('manager.webcastSetup.media.uploadSlides')}
          buttonContent={(isOpen) =>
            isOpen ? (
              hasContent ? (
                <>
                  {t('manager.webcastSetup.media.hideUpload')}
                  <InfoTooltip label={t('manager.webcastSetup.media.presentation.multiplePresentationsTooltip')} />
                </>
              ) : (
                t('manager.webcastSetup.media.hideUpload')
              )
            ) : hasContent ? (
              <>
                {t('manager.webcastSetup.media.uploadAdditionalSlides')}
                <InfoTooltip label={t('manager.webcastSetup.media.presentation.multiplePresentationsTooltip')} />
              </>
            ) : (
              t('manager.webcastSetup.media.uploadSlides')
            )
          }
        >
          {fileUploader(primaryLang.code)}
        </MediaAccordion>
      )}

      {isMultiLanguage &&
        uniqueGroupIds.map((groupId) => (
          <Tabs key={groupId} compact={languages.length <= 4}>
            {languages.map((language) => {
              const [currentLanguagePresentation] = presentations
                .filter((presentation) => presentation.language === language.code)
                .filter((presentation) => presentation.groupId === groupId);

              const { processingFinished } = getPresentationStatus(currentLanguagePresentation);

              return (
                <Tab
                  key={language.code}
                  data-testid={`presentation-tab-${language.code}`}
                  label={
                    <>
                      {getLanguageLabel(language.code)}
                      {processingFinished && <CheckIcon className={styles.presentationExistsIcon} />}
                    </>
                  }
                >
                  {currentLanguagePresentation && (
                    <div className={styles.presentationsList}>
                      <Presentation
                        key={currentLanguagePresentation.id}
                        presentation={currentLanguagePresentation}
                        disablePresentationDeletion={disablePresentationDeletion}
                        onLoadStart={onLoadStart}
                        onLoadEnd={onLoadEnd}
                      />
                    </div>
                  )}

                  {!currentLanguagePresentation && (
                    <MediaAccordion
                      testId={`presentation-${language.code}`}
                      className={styles.mediaAccordion}
                      visible
                      buttonLabel={t('manager.webcastSetup.media.uploadSlides')}
                      buttonContent={(isOpen) =>
                        isOpen
                          ? t('manager.webcastSetup.media.hideUpload')
                          : t('manager.webcastSetup.media.uploadSlides')
                      }
                    >
                      {fileUploader(language.code, groupId)}
                    </MediaAccordion>
                  )}
                </Tab>
              );
            })}
          </Tabs>
        ))}

      {isMultiLanguage && (!hasContent || allUploaded) && (
        <Tabs compact={languages.length <= 4}>
          {languages.map((language) => (
            <Tab
              key={language.code}
              data-testid={`presentation-tab-${language.code}`}
              label={getLanguageLabel(language.code)}
            >
              <MediaAccordion
                testId={`presentation-${language.code}`}
                className={styles.mediaAccordion}
                visible
                buttonLabel={t('manager.webcastSetup.media.uploadSlides')}
                buttonContent={(isOpen) => (isOpen ? additionalSetOpenLabel : additionalSetCloseLabel)}
              >
                {fileUploader(language.code)}
              </MediaAccordion>
            </Tab>
          ))}
        </Tabs>
      )}

      {noteVisible && (
        <Note data-testid="multilanguage-note" variant="primary" style={{ whiteSpace: 'pre-line', maxWidth: '100%' }}>
          {t('manager.webcastSetup.media.presentation.multilanguageNote')}
        </Note>
      )}
    </div>
  );
}
